import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Paper,
} from '@mui/material';
import { Launch as LaunchIcon } from '@mui/icons-material';
import { statusColors } from '../App';
import { apiService } from '../services/apiService';

interface Batch {
  id: string;
  batch_number: string;
  work_order_id: string | null;
  status: string;
  product_name: string;
  product_code: string;
  mbr_number: string;
  line_name: string;
  batch_size_value: number;
  batch_size_unit: string;
  created_at: string;
  actual_start: string | null;
  actual_end: string | null;
}

const WorkOrders: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [woFilter, setWoFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const data = await apiService.getBatches();
      setBatches(data.results || data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || '#6B7280';
  };

  // Get unique WO IDs for filter
  const woIds = Array.from(new Set(batches.map(b => b.work_order_id).filter(Boolean))) as string[];

  // Apply filters
  const filtered = batches.filter(b => {
    if (woFilter.length > 0 && !woFilter.includes(b.work_order_id || '')) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(b.status)) return false;
    return true;
  });

  // Sort by WO ID then batch number
  const sorted = [...filtered].sort((a, b) => {
    const woCompare = (a.work_order_id || '').localeCompare(b.work_order_id || '');
    if (woCompare !== 0) return woCompare;
    return a.batch_number.localeCompare(b.batch_number);
  });

  // Track which WO IDs we've already rendered (for visual grouping)
  const renderedWOs = new Set<string>();

  // Count batches per WO
  const woCounts: Record<string, number> = {};
  sorted.forEach(b => {
    const wo = b.work_order_id || 'Unassigned';
    woCounts[wo] = (woCounts[wo] || 0) + 1;
  });

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom>
        Work Orders
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        ERP work orders and their associated production batches
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Work Order</InputLabel>
              <Select
                multiple
                value={woFilter}
                label="Work Order"
                onChange={e => setWoFilter(e.target.value as string[])}
                renderValue={selected => (selected as string[]).join(', ')}
              >
                {woIds.map(wo => (
                  <MenuItem key={wo} value={wo}>{wo} ({woCounts[wo] || 0} batches)</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={statusFilter}
                label="Status"
                onChange={e => setStatusFilter(e.target.value as string[])}
                renderValue={selected => (selected as string[]).join(', ')}
              >
                {['pending', 'in_progress', 'paused', 'completed', 'approved', 'rejected'].map(s => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_', ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(woFilter.length > 0 || statusFilter.length > 0) && (
              <Button variant="outlined" size="small" onClick={() => { setWoFilter([]); setStatusFilter([]); }}>
                Clear
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
                <TableCell>Work Order ID</TableCell>
                <TableCell>Batch Number</TableCell>
                <TableCell>MBR</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Line</TableCell>
                <TableCell>Batch Size</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary">No work orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((batch, idx) => {
                  const wo = batch.work_order_id || 'Unassigned';
                  const isFirstInWO = !renderedWOs.has(wo);
                  const rowSpan = isFirstInWO ? woCounts[wo] : 0;
                  if (isFirstInWO) renderedWOs.add(wo);

                  return (
                    <TableRow key={batch.id} hover sx={{
                      borderTop: isFirstInWO && idx > 0 ? '2px solid #374151' : undefined,
                    }}>
                      {isFirstInWO && (
                        <TableCell
                          rowSpan={rowSpan}
                          sx={{
                            verticalAlign: 'top',
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            backgroundColor: 'rgba(59,130,246,0.05)',
                            borderRight: '2px solid #374151',
                            pt: 2,
                          }}
                        >
                          {wo}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {rowSpan} batch{rowSpan > 1 ? 'es' : ''}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{batch.batch_number}</Typography>
                      </TableCell>
                      <TableCell>{batch.mbr_number}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{batch.product_name}</Typography>
                        <Typography variant="caption" color="textSecondary">{batch.product_code}</Typography>
                      </TableCell>
                      <TableCell>{batch.line_name}</TableCell>
                      <TableCell>{batch.batch_size_value} {batch.batch_size_unit}</TableCell>
                      <TableCell>
                        <Chip
                          label={batch.status.replace('_', ' ')}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(batch.status),
                            color: 'white',
                            textTransform: 'capitalize',
                            fontWeight: 'medium',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(batch.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LaunchIcon />}
                          onClick={() => navigate(`/batch-execution/${batch.id}`)}
                          sx={{ textTransform: 'none' }}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WorkOrders;
